import React, { useState, useEffect } from "react";

const DeviceForm = ({ initialValues, onSubmit, onCancel, isEditMode = false }) => {
  const [deviceId, setDeviceId] = useState(initialValues?.device_id || "");
  const [deviceName, setDeviceName] = useState(initialValues?.device_name || "");
  const [deviceMac, setDeviceMac] = useState(initialValues?.device_mac || "");
  const [isActive, setIsActive] = useState(initialValues?.is_active ?? true);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (initialValues) {
      setDeviceId(initialValues.device_id || "");
      setDeviceName(initialValues.device_name || "");
      setDeviceMac(initialValues.device_mac || "");
      setIsActive(initialValues.is_active ?? true);
    } else {
      setDeviceId("");
      setDeviceName("");
      setDeviceMac("");
      setIsActive(true);
      setErrors({});
    }
  }, [initialValues]);

  const validateForm = () => {
    let tempErrors = {};
    if (!deviceName) tempErrors.device_name = "Device Name is required";
    if (!deviceMac) tempErrors.device_mac = "Device Mac is required";
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const deviceData = {
        device_name: deviceName,
        device_mac: deviceMac,
        is_active: isActive,
      };

      await onSubmit(deviceData);
    } catch (err) {
      console.error("Form submission error:", err);
      setErrors({
        form: err.message || "Failed to submit the form. Please check console for details.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm">
      {errors.form && (
        <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6 mx-6">
          {errors.form}
        </div>
      )}

      <div className="p-6 space-y-6">
        <div className="grid bg-gray-50 p-6 rounded-lg grid-cols-1 md:grid-cols-2 gap-6">
          {isEditMode && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Device ID
              </label>
              <input
                type="text"
                value={deviceId}
                disabled
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-100"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Device Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              required
              className={`w-full px-4 py-2 border ${errors.device_name ? "border-red-500" : "border-gray-300"
                } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
            />
            {errors.device_name && (
              <p className="mt-1 text-sm text-red-500">{errors.device_name}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mac Address <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={deviceMac}
              onChange={(e) => setDeviceMac(e.target.value)}
              required
              className={`w-full px-4 py-2 border ${errors.device_mac ? "border-red-500" : "border-gray-300"
                } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
            />
            {errors.device_mac && (
              <p className="mt-1 text-sm text-red-500">{errors.device_mac}</p>
            )}
          </div>

          {isEditMode && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={isActive ? 'Active' : 'Inactive'}
                onChange={(e) => setIsActive(e.target.value === 'Active')}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
          )}
        </div>
      </div>

      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-4 rounded-b-lg sticky bottom-0">
        <button
          type="button"
          onClick={handleCancel}
          className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          onClick={handleSubmit}
          disabled={loading}
          className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading
            ? isEditMode
              ? "Saving..."
              : "Adding..."
            : isEditMode
              ? "Save Changes"
              : "Add Device"}
        </button>
      </div>
    </div>
  );
};

export default DeviceForm;